export type ClipWithVersion = {
  id: string;
  source_clip_id?: string | null;
  version_number?: number | null;
  createdAt: string;
};

export type VersionGroup<T extends ClipWithVersion> = {
  /** The clip to display (latest version) */
  displayClip: T;
  /** All versions sorted newest-first (including displayClip) */
  allVersions: T[];
  /** Total number of versions */
  totalVersions: number;
};

export type ClipVersionGroups<T extends ClipWithVersion> = {
  groups: VersionGroup<T>[];
  getGroupForClip: (clipId: string) => VersionGroup<T> | undefined;
};

/**
 * Groups clips by version chain.
 * Clips with the same source_clip_id (or that ARE the source) are grouped together.
 * The latest version of each group is the "display" clip; all versions stay
 * accessible via the group.
 */
export function groupClipVersions<T extends ClipWithVersion>(clips: T[]): ClipVersionGroups<T> {
  // Build version groups: key is the root clip id
  const groupMap = new Map<string, T[]>();

  for (const clip of clips) {
    // The root id is source_clip_id if present, otherwise the clip itself is the root
    const rootId = clip.source_clip_id || clip.id;
    const group = groupMap.get(rootId);
    if (group) {
      group.push(clip);
    } else {
      groupMap.set(rootId, [clip]);
    }
  }

  // Sort each group by version_number descending (latest first), fallback to createdAt
  const groups: VersionGroup<T>[] = [];
  const clipToGroup = new Map<string, VersionGroup<T>>();

  for (const [, versionClips] of groupMap) {
    const sorted = [...versionClips].sort((a, b) => {
      const vA = a.version_number ?? 1;
      const vB = b.version_number ?? 1;
      if (vA !== vB) return vB - vA;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    const group: VersionGroup<T> = {
      displayClip: sorted[0],
      allVersions: sorted,
      totalVersions: sorted.length,
    };

    groups.push(group);
    for (const clip of sorted) {
      clipToGroup.set(clip.id, group);
    }
  }

  return {
    groups,
    getGroupForClip: (clipId: string) => clipToGroup.get(clipId),
  };
}

/** Ids of every non-latest version — hidden from the grid, reachable via the version badge. */
export function collectHiddenVersionIds<T extends ClipWithVersion>(
  groups: VersionGroup<T>[],
): Set<string> {
  const hidden = new Set<string>();
  for (const group of groups) {
    for (let i = 1; i < group.allVersions.length; i++) {
      hidden.add(group.allVersions[i].id);
    }
  }
  return hidden;
}
